/**
 * ONE ACTIVE TAB PER ACCOUNT.
 *
 * THE BUG THIS FIXES: two tabs (or a tab and a forgotten one) playing the same account both autosaved, and
 * the server's 409 handling (sim/save.ts `sendSave`) can only pick ONE whole snapshot — newest `savedAt` wins.
 * So the tab that acted last always won and the other tab's progress vanished, with the lost-save notice
 * showing up only some of the time. Two histories of one garden cannot be merged; the fix is to never let a
 * second one be written.
 *
 * So the browser holds a Web Lock, `shelf-garden:play:<owner>` (owner = the save owner id, "anon" when there
 * is none), for the tab's whole life. The tab that gets it is ACTIVE and saves as before. A tab that cannot
 * get it is PASSIVE: `saveBlockedByOtherTab()` is true, which stops `apiStore.save` and App's autosave and
 * close flush, and App covers the game with a card ("open in another tab — Play here").
 *
 * "PLAY HERE" asks the active tab over a BroadcastChannel to flush its save and let go; it turns passive (the
 * same card, reversed) and this tab reloads — a reload, not a hand-over in place, because this tab's game in
 * memory is older than everything the other tab just played, and its first autosave would write it back.
 *
 * Where `navigator.locks` is missing the same channel stands in for the lock: a new tab asks "who holds it?"
 * and a live holder answers. Weaker (two tabs opened in the same instant can both win), but never worse than
 * no guard. Modes that never save — the DEV `?shot=` presets, the spectate mirror, a garden visit, the test
 * server's "Live save (read-only)" — never claim, stay in state "none", and are never blocked.
 */

export type PlayLockState = "none" | "active" | "passive";

/** How long a NEW tab waits for a held lock before giving up and turning passive. Not zero: a reload (and
 *  Playwright's `goto`) starts the next document while the old one is still letting go of its lock, and an
 *  `ifAvailable` that loses that race would leave a player's only tab locked out of its own save. */
const CLAIM_WAIT_MS = 1500;
/** "Play here": how long to wait for the active tab to flush and release before reloading anyway (a tab
 *  frozen in the background can't answer; the reload's own claim then decides). */
const TAKE_WAIT_MS = 4000;
/** Fallback only (no `navigator.locks`): how long a new tab listens for a holder's answer. */
const WHO_WAIT_MS = 300;

type Msg = { t: "who" } | { t: "held" } | { t: "take" } | { t: "released" };

let _state: PlayLockState = "none";
let _owner: string | null = null;
let _claim: Promise<PlayLockState> | null = null;
let _release: (() => void) | null = null;
let _channel: BroadcastChannel | null = null;
let _flush: (() => Promise<void> | void) | null = null;
let _handedOff = false;
const listeners = new Set<(s: PlayLockState) => void>();

function setState(s: PlayLockState): void {
  if (_state === s) return;
  _state = s;
  for (const fn of listeners) fn(s);
}

export function playLockState(): PlayLockState { return _state; }
export function onPlayLock(fn: (s: PlayLockState) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
/** True only in a tab that tried for the lock and does not hold it — every save path checks this. */
export function saveBlockedByOtherTab(): boolean { return _state === "passive"; }
/** True once this tab was ACTIVE and handed play to another tab ("Play here" over there) — the card reads
 *  "You're playing in another tab" rather than "open in another tab". */
export function handedOff(): boolean { return _handedOff; }

/** App registers how to write its game NOW, so the active tab can flush before handing over. */
export function setPlayLockFlush(fn: (() => Promise<void> | void) | null): void { _flush = fn; }

function lockName(owner: string): string { return `shelf-garden:play:${owner}`; }
function locks(): LockManager | null {
  return typeof navigator !== "undefined" && navigator.locks ? navigator.locks : null;
}
function post(msg: Msg): void { try { _channel?.postMessage(msg); } catch { /* channel closed */ } }

/** Request the lock; resolves true once held (and keeps holding it until `_release`), false if not got. */
function requestLock(name: string, wait: boolean): Promise<boolean> {
  const lm = locks();
  if (!lm) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    const ctl = wait ? new AbortController() : null;
    const timer = ctl ? window.setTimeout(() => ctl.abort(), CLAIM_WAIT_MS) : undefined;
    const opts: LockOptions = wait ? { signal: ctl!.signal } : { ifAvailable: true };
    lm.request(name, opts, (lock) => {
      window.clearTimeout(timer);
      if (!lock) { resolve(false); return; }
      resolve(true);
      return new Promise<void>((r) => { _release = r; });
    }).catch(() => { window.clearTimeout(timer); resolve(false); });
  });
}

/** The holder's half of the channel: answer "who", and on "take" flush, let go, and turn passive. */
async function onMessage(e: MessageEvent<Msg>): Promise<void> {
  const m = e.data;
  if (_state !== "active") return;
  if (m?.t === "who") { post({ t: "held" }); return; }
  if (m?.t !== "take") return;
  try { await _flush?.(); } catch { /* the release must still happen */ }
  _handedOff = true;
  setState("passive");             // before the release: nothing may write once the other tab can claim
  _release?.(); _release = null;
  post({ t: "released" });
}

/**
 * Claim the play lock for this tab and owner. Idempotent per owner (GameLoader calls it on every boot
 * attempt, twice under StrictMode); a different owner lets the old lock go and claims afresh.
 */
export function claimPlayLock(owner: string | null): Promise<PlayLockState> {
  const id = owner && owner.length ? owner : "anon";
  if (_claim && _owner === id) return _claim;
  _release?.(); _release = null;
  _channel?.close(); _channel = null;
  _owner = id;
  const name = lockName(id);
  if (typeof BroadcastChannel !== "undefined") {
    _channel = new BroadcastChannel(name);
    _channel.onmessage = (e) => { void onMessage(e as MessageEvent<Msg>); };
  }
  _claim = (async () => {
    let held: boolean;
    if (locks()) {
      held = (await requestLock(name, false)) || (await requestLock(name, true));
    } else if (_channel) {
      const ch = _channel;
      held = await new Promise<boolean>((resolve) => {
        const t = window.setTimeout(() => { ch.removeEventListener("message", on); resolve(true); }, WHO_WAIT_MS);
        const on = (e: MessageEvent<Msg>) => {
          if (e.data?.t !== "held") return;
          window.clearTimeout(t); ch.removeEventListener("message", on); resolve(false);
        };
        ch.addEventListener("message", on);
        post({ t: "who" });
      });
    } else {
      held = true;                 // no lock and no channel: nothing to coordinate with
    }
    if (_owner !== id) return _state;   // superseded by another owner's claim meanwhile
    setState(held ? "active" : "passive");
    return _state;
  })();
  return _claim;
}

/**
 * "PLAY HERE" from a passive tab: ask the holder to flush and release, wait until it has (or until
 * TAKE_WAIT_MS), then reload — the reload's claim takes the free lock and loads the save it just wrote.
 */
export async function takePlayLock(): Promise<void> {
  if (_state !== "passive" || !_owner) return;
  const name = lockName(_owner);
  const ch = _channel;
  await new Promise<void>((resolve) => {
    const t = window.setTimeout(done, TAKE_WAIT_MS);
    function done() { window.clearTimeout(t); ch?.removeEventListener("message", on); resolve(); }
    function on(e: MessageEvent<Msg>) { if (e.data?.t === "released") done(); }
    ch?.addEventListener("message", on);
    post({ t: "take" });
    // Granted the moment the holder lets go (or at once if it has already gone) — then let go ourselves.
    locks()?.request(name, () => { done(); }).catch(() => {});
  });
  window.location.reload();
}
